import { useMemo } from 'react';

import {
  Button,
  HStack,
  IconButton,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Spacer,
  Text,
  VStack,
} from '@chakra-ui/react';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import { CloseCircleOutline, useToast } from '@aslanpay/portal-common-lib';

import useFetchIntegrationConfigSpec from 'app/hooks/useFetchIntegrationConfigSpec';
import useUpdateIntegrationStatus from 'app/hooks/useUpdateIntegrationStatus';

import {
  AuthConfigFields,
  ConnectionFormValues,
  ConnectionSettingsFields,
  buildAuthConfig,
  buildConfig,
  buildEditSchema,
  configToEntries,
  findMissingRequiredConfigFields,
} from '../IntegrationForm';

import { EditIntegrationModalProps } from './types';

function EditIntegrationModal({
  companyId,
  integration,
  isOpen,
  onClose,
}: EditIntegrationModalProps) {
  const toast = useToast();
  const updateMutation = useUpdateIntegrationStatus();
  const { data: specFields = [] } = useFetchIntegrationConfigSpec(integration.provider);
  const integrationTypes = integration.types.map((type) => type.integrationType);

  const originalAuthType = integration.authType ?? 'NONE';

  const schema = useMemo(
    () =>
      buildEditSchema({
        originalAuthType,
        configuredSecretFields: integration.configuredSecretFields,
      }),
    [originalAuthType, integration.configuredSecretFields]
  );

  const methods = useForm<ConnectionFormValues>({
    mode: 'onChange',
    resolver: yupResolver(schema),
    defaultValues: {
      baseUrl: integration.baseUrl ?? '',
      configValues: {},
      configEntries: configToEntries(integration.config),
      authType: originalAuthType,
      headerName: integration.authHeaderName ?? '',
      username: integration.authUsername ?? '',
      tokenUrl: integration.authTokenUrl ?? '',
      clientId: integration.authClientId ?? '',
      scope: integration.authScope ?? '',
    },
  });

  const {
    handleSubmit,
    reset,
    setError,
    formState: { isValid },
  } = methods;

  const handleClose = () => {
    reset();
    onClose();
  };

  const onSubmit = async (data: ConnectionFormValues) => {
    const missingFields = findMissingRequiredConfigFields(
      specFields,
      { protocol: integration.protocol, integrationTypes },
      data.configValues
    );
    if (missingFields.length > 0) {
      missingFields.forEach((field) =>
        setError(`configValues.${field.key}`, { message: `${field.key} is required` })
      );
      return;
    }

    try {
      await updateMutation.mutateAsync({
        id: integration.id,
        companyId,
        body: {
          status: integration.status,
          // Always sent: an emptied base URL clears back to the provider default, and the settings map
          // replaces the stored one.
          baseUrl: data.baseUrl ?? '',
          config: buildConfig(data.configEntries, data.configValues) ?? {},
          authConfig: buildAuthConfig(data),
        },
      });
      toast({
        position: 'top-right',
        status: 'success',
        title: 'Integration updated',
        description: 'The partner integration has been successfully updated.',
      });
      handleClose();
    } catch {
      toast({
        position: 'top-right',
        status: 'error',
        title: 'Something went wrong',
        description: 'Sorry, the integration could not be updated. Please try again.',
      });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} isCentered closeOnOverlayClick={false}>
      <ModalOverlay />
      {/* The shared theme sets the dialog width to auto (content-hugging), so the two-column width
          must be explicit or the modal only grows once rows are added. */}
      <ModalContent w={{ base: '100vw', sm: '90vw', md: '760px' }} maxH="85vh" overflowY="auto">
        <ModalHeader>
          <HStack>
            <Text as="h6">Edit partner integration</Text>
            <Spacer />
            <IconButton
              variant="link"
              icon={<CloseCircleOutline boxSize="30px" />}
              aria-label="Close edit integration"
              onClick={handleClose}
            />
          </HStack>
        </ModalHeader>
        <ModalBody>
          <Text textStyle="extraSmallRegular" color="brand.darkGray" mb="16px">
            {integration.provider} · {integration.protocol} connection. Secrets are write-only:
            fields marked as currently set keep their stored value when left blank.
          </Text>
          <FormProvider {...methods}>
            <form id="edit-integration-form" onSubmit={handleSubmit(onSubmit)} noValidate>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing="24px" alignItems="flex-start">
                <VStack spacing="16px" align="stretch">
                  <ConnectionSettingsFields
                    provider={integration.provider}
                    protocol={integration.protocol}
                    integrationTypes={integrationTypes}
                  />
                </VStack>
                <VStack spacing="16px" align="stretch">
                  <AuthConfigFields
                    originalAuthType={originalAuthType}
                    configuredSecretFields={integration.configuredSecretFields}
                  />
                </VStack>
              </SimpleGrid>
            </form>
          </FormProvider>
        </ModalBody>
        <ModalFooter>
          <HStack>
            <Button variant="outline" size="sm" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              form="edit-integration-form"
              size="sm"
              isDisabled={!isValid}
              isLoading={updateMutation.isPending}
            >
              Save changes
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default EditIntegrationModal;
